import { Show } from "solid-js";
import { Check } from "@fleet/icons";
import { cn } from "@apunte-digital/shared";
import { RTL_LOCALES, type SupportedLocale } from "@apunte-digital/config/locales";

interface LocaleCardProps {
  code: SupportedLocale;
  flag: string;
  label: string;
  englishLabel: string;
  isActive: boolean;
  isLoading: boolean;
  onSelect: (code: SupportedLocale) => void;
}

export function LocaleCard(props: LocaleCardProps) {
  const isRTL = () => RTL_LOCALES.has(props.code);

  const handleSelect = () => {
    props.onSelect(props.code);
  };

  return (
    <button
      type="button"
      id={`locale-${props.code}`}
      role="radio"
      aria-checked={props.isActive}
      disabled={props.isLoading && !props.isActive}
      onClick={handleSelect}
      class={cn(
        "group relative flex items-center gap-4 rounded-xl border p-4 text-left transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm",
        props.isActive
          ? "border-primary bg-primary/10 shadow-md shadow-primary/10"
          : "border-border bg-card",
        props.isLoading && !props.isActive && "cursor-not-allowed opacity-60",
      )}
    >
      {/* Flag */}
      <span
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-2xl shadow-sm transition-transform group-hover:scale-110"
        aria-hidden="true"
      >
        {props.flag}
      </span>

      {/* Labels */}
      <div class="flex-1 min-w-0">
        <p
          class={cn(
            "font-semibold text-sm leading-tight truncate",
            props.isActive && "text-primary",
          )}
          dir={isRTL() ? "rtl" : "ltr"}
        >
          {props.label}
        </p>
        <p class="text-xs text-muted-foreground mt-0.5 truncate">
          {props.englishLabel}
          {isRTL() && (
            <span class="ml-1.5 inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
              RTL
            </span>
          )}
        </p>
      </div>

      {/* Active indicator */}
      <Show when={props.isActive}>
        <div class="shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md animate-in zoom-in-50 duration-200">
          <Check class="h-3.5 w-3.5" />
        </div>
      </Show>
    </button>
  );
}
